import React from "react"
import { graphql } from "gatsby"
import { TransitionPortal } from "gatsby-plugin-transition-link"

import { ALIGN } from "../contentful/globals"
import Meta from "./meta"
import Container from "../global/container"
import HorizontalScroller from "../global/horizontal"
import Modules from "../contentful/modules/modules"
import PageAnimation from "../atomic/organisms/PageAnimation"
import Footer from "./footer"

export default class extends React.Component {

  state = {
    branding: true
  }

  pageAnimationEl = React.createRef()

  transition = {
    exit: {
      length: 3,
      trigger: ({ exit }) => this.animate(),
    },
    entry: {
      delay: 1.5,
    },
  }

  componentDidMount(){
    if (typeof window !== `undefined`){
      let params = new URLSearchParams(window.location.search)
      if(params.get('hide') === `true`){
        this.setState({ branding: false })
      }
    }
  }

  createContentfulModule(__typename, i, props) {
    return (ContentfulModule => (
      <ContentfulModule key={i} {...props} transition={this.transition} />
    ))(Modules[__typename])
  }

  animate() {
    this.pageAnimationEl.current.animate()
  }

  render() {
    const { direction, meta, modules } = this.props.data.contentfulPage

    return (
      <React.Fragment>
        <Meta
          title={meta.title}
          description={meta.description}
          keywords={meta.keywords}
          image={meta.image.file.url}
        />
        {direction === ALIGN.HORIZONTAL && (
          <HorizontalScroller transition={this.transition}>
            {modules.map((module, i) =>
              this.createContentfulModule(module.__typename, i, module)
            )}
            {this.state.branding && <Footer />}
          </HorizontalScroller>
        )}
        {direction === ALIGN.VERTICAL && (
          <Container direction={direction} transition={this.transition}>
            {modules.map((module, i) =>
              this.createContentfulModule(module.__typename, i, module)
            )}
          </Container>
        )}
        <TransitionPortal>
          <PageAnimation ref={this.pageAnimationEl} />
        </TransitionPortal>
      </React.Fragment>
    )
  }
}

export const pageQuery = graphql`
  query PageQuery($contentful_id: String!) {
    contentfulPage(contentful_id: { eq: $contentful_id }) {
      direction
      meta {
        ... on ContentfulMeta {
          title
          description
          image {
            file {
              url
            }
          }
        }
      }
      modules {
        ... on ContentfulModuleHeader {
          __typename
          contentful_id
          title
          subTitle
          services
          image {
            fluid(maxWidth: 2400, quality: 100) {
              ...GatsbyContentfulFluid_withWebp
            }
          }
          backgroundVideo {
            file {
              url
            }
          }
          mobileVideo {
            file {
              url
            }
          }
          video {
            file {
              url
            }
          }
        }
        ... on ContentfulModuleArticle {
          article {
            title
            slug
            short
            thumbnail {
              fluid(maxWidth: 1200, quality: 100) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
            images {
              fluid(maxWidth: 1200, quality: 100) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
            body {
              json
            }
            date
            tags
            tools {
              fluid(maxWidth: 1200, quality: 100) {
                ...GatsbyContentfulFluid_withWebp
              }
            }
            facts
            related {
              title
              slug
              short
              thumbnail {
                fluid(maxWidth:700, quality:85){
                  ...GatsbyContentfulFluid_withWebp
                }
              }
            }
          }
        }
        ... on ContentfulModuleProjectSection {
          mode
          blendLeft
          blendRight
          backgroundText
          backgroundTextPosition
          blocks {
            ... on ContentfulBlockText {
              contentful_id
              align
              accent
              title
              description {
                json
              }
            }
            ... on ContentfulBlockMedia {
              contentful_id
              type
              ratio
              align
              media {
                __typename
                fluid(maxWidth: 2400, quality: 100) {
                  ...GatsbyContentfulFluid_withWebp
                }
                file {
                  contentType
                  url
                }
              }
              placeholder{
                file{
                  url
                }
              }
              label
              width
              height
              maxHeight
              playable
              decoration
              text
            }
            ... on ContentfulBlockCarousel {
              mediaBlocks {
                contentful_id
                type
                ratio
                align
                media {
                  __typename
                  fluid(maxWidth: 2400, quality: 100) {
                    ...GatsbyContentfulFluid_withWebp
                  }
                  file {
                    contentType
                    url
                  }
                }
                placeholder{
                  file{
                    url
                  }
                }
                label
                width
                height
                maxHeight
                playable
                decoration
                text
              }
            }
            ... on ContentfulBlockInstagramCarousel {
              mediaBlocks {
                contentful_id
                type
                ratio
                align
                media {
                  __typename
                  fluid(maxWidth: 2400, quality: 100) {
                    ...GatsbyContentfulFluid_withWebp
                  }
                  file {
                    contentType
                    url
                  }
                }
                placeholder{
                  file{
                    url
                  }
                }
                label
                width
                height
                maxHeight
                playable
                decoration
                text
              }
            }
            ... on ContentfulBlockMediaAndCarousel {
              mediaBlock {
                contentful_id
                type
                ratio
                align
                media {
                  __typename
                  fluid(maxWidth: 2400, quality: 100) {
                    ...GatsbyContentfulFluid_withWebp
                  }
                  file {
                    contentType
                    url
                  }
                }
                placeholder{
                  file{
                    url
                  }
                }
                label
                width
                height
                maxHeight
                playable
                decoration
                text
              }
              gallery {
                contentful_id
                type
                ratio
                align
                media {
                  __typename
                  fluid(maxWidth: 2400, quality: 100) {
                    ...GatsbyContentfulFluid_withWebp
                  }
                  file {
                    contentType
                    url
                  }
                }
                placeholder{
                  file{
                    url
                  }
                }
                label
                width
                height
                maxHeight
                playable
                decoration
                text
              }
            }
            ... on ContentfulBlockResults {
              results {
                largeText
                smallText
                image {
                  file {
                    url
                  }
                }
              }
            }
            ... on ContentfulBlockSuggestedProjects{
              label
            }
            ... on ContentfulBlockClients{
              logos{
                fluid(maxWidth: 1000, quality: 100) {
                  ...GatsbyContentfulFluid_withWebp
                }
                file{
                  url
                }
              }
            }
            ... on ContentfulBlockFocus {
              __typename
              title
              items {
                title
                description
                project {
                  __typename
                  title
                  slug
                  client
                  portraitImage {
                    fluid(maxWidth: 2400, quality: 100) {
                      ...GatsbyContentfulFluid_withWebp
                    }
                  }
                }
              }
            }
            ... on ContentfulBlockAllProjectList {
              title
              accent
            }
            ... on ContentfulBlockProjectList {
              title
              accent
              layout
              projects{
                title
                slug
                client
                services
                portraitImage{
                  fluid(maxWidth: 1200, quality: 100) {
                    ...GatsbyContentfulFluid_withWebp
                  }
                }
                landscapeImage{
                  fluid(maxWidth: 1200, quality: 100) {
                    ...GatsbyContentfulFluid_withWebp
                  }
                }
                award
              }
            }
            ...on ContentfulBlockLatestArticles{
              title
              accent
              description{
                json
              }
            }
          }
        }
        ... on ContentfulModuleServiceSection {
          mode
          blocks {
            ... on ContentfulBlockServiceList {
              __typename
              align
              title
              list
            }
          }
        }
      }
    }
  }
`
